import { ParsedAuthResult, userMessageForAuthResponse } from "./australian-super-auth";

/** Thrown from Super sync paths when the server session is gone or a timeout occurred. */
export class SuperPluginTerminalError extends Error {
  readonly userMessage: string;

  constructor(userMessage: string) {
    super(userMessage);
    this.name = "SuperPluginTerminalError";
    this.userMessage = userMessage;
  }
}

const TERMINAL_SYNC_MARKERS = [
  "scrape_timeout",
  "verify_timeout",
  "post_otp_ui_timeout",
  "otp_attempts_exceeded",
  "otp_idle_timeout",
  "Invalid or expired identifier",
  "session has been closed",
  "took too long",
  "timed out",
  "service_unavailable",
] as const;

/** Whether an error message from accounts/transactions/auth network paths is terminal. */
export function isSuperPluginTerminalErrorMessage(message: string): boolean {
  const m = (message || "").trim();
  if (!m) {
    return false;
  }
  return TERMINAL_SYNC_MARKERS.some((marker) => m.includes(marker));
}

/** User-facing copy for sync / fetch failures (aligns with glossapp AUTH_USER_MESSAGES where applicable). */
export function messageForSuperPluginSyncFailure(error: unknown): string {
  if (error instanceof SuperPluginTerminalError) {
    return error.userMessage;
  }
  const raw =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Sync failed. Please connect and try again.";
  if (isSuperPluginTerminalErrorMessage(raw)) {
    if (raw.includes("scrape_timeout")) {
      return userMessageForAuthResponse("scrape_timeout");
    }
    if (raw.includes("verify_timeout")) {
      return userMessageForAuthResponse("verify_timeout");
    }
    if (raw.includes("post_otp_ui_timeout")) {
      return userMessageForAuthResponse("post_otp_ui_timeout");
    }
    if (raw.includes("otp_attempts_exceeded")) {
      return userMessageForAuthResponse("otp_attempts_exceeded");
    }
    if (raw.includes("otp_idle_timeout") || raw.includes("session has been closed")) {
      return userMessageForAuthResponse("otp_idle_timeout");
    }
    if (raw.includes("service_unavailable")) {
      return userMessageForAuthResponse("service_unavailable");
    }
    return raw;
  }
  return "Sync failed. Please connect and try again.";
}

export function isParsedAuthTerminal(parsed: ParsedAuthResult): boolean {
  return parsed.kind === "terminal";
}
